import { ChevronRight, type LucideIcon } from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';

/**
 * Ein Abschnitt der Kontextspalte: Kopf zum Auf- und Zuklappen, zugeklappt mit einem Hinweis,
 * was darin steht. Die Spalte zeigt zuerst den Aufenthalt; alles Weitere ist einen Klick entfernt
 * statt eine Kartenwand.
 */
export function PanelCard({
  icon: Icon,
  title,
  hint,
  defaultOpen = false,
  trailing,
  children,
}: {
  icon: LucideIcon;
  title: string;
  /** Kurzer Inhaltshinweis im zugeklappten Zustand. */
  hint?: string;
  defaultOpen?: boolean;
  trailing?: ReactNode;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const bodyId = useId();
  return (
    <section className="border-hairline border-b last:border-b-0">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={() => setOpen((value) => !value)}
        className="hover:bg-card-alt/60 focus-visible:ring-sage flex min-h-[52px] w-full items-center gap-2.5 px-4 py-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset"
      >
        <Icon size={16} aria-hidden className="text-ink-soft flex-none" />
        <span className="min-w-0 flex-1">
          <span className="text-ink block text-[13px] font-bold">{title}</span>
          {!open && hint && <span className="text-ink-soft block truncate text-xs">{hint}</span>}
        </span>
        {open && trailing}
        <ChevronRight
          size={16}
          aria-hidden
          className={cn(
            'text-ink-soft flex-none transition-transform motion-reduce:transition-none',
            open && 'rotate-90',
          )}
        />
      </button>
      {open && (
        <div id={bodyId} className="px-4 pb-4">
          {children}
        </div>
      )}
    </section>
  );
}

// Kompaktes Feld-Grid (2-spaltig, wide fuer volle Breite).
export function FieldGrid({ children }: { children: ReactNode }) {
  return <dl className="grid grid-cols-2 gap-x-4 gap-y-3">{children}</dl>;
}

export function Field({
  label,
  value,
  wide,
  mono,
}: {
  label: string;
  value: ReactNode;
  wide?: boolean;
  mono?: boolean;
}) {
  return (
    <div className={wide ? 'col-span-2' : undefined}>
      <dt className="text-ink-faint text-[11px] font-semibold">{label}</dt>
      <dd className={cn('text-ink mt-0.5 text-[13px] font-medium', mono && 'font-mono')}>
        {value ?? <span className="text-ink-faint font-normal">—</span>}
      </dd>
    </div>
  );
}
